import { createBrowserHistory } from 'history';
import { createElement } from 'react';
import { hydrate, render } from 'react-dom';
import { addLocaleData } from 'react-intl';
import * as enLocaleData from 'react-intl/locale-data/en';
import * as jaLocaleData from 'react-intl/locale-data/ja';
import Store from 'repatch';
import register from 'timeago.js';
import { registerCustomElements } from './register-custom-elements';
import GoogleAnalytics from './adapter/GoogleAnalytics';
import FirebaseClient from './adapter/Firebase';
import GraphQLClient from './adapter/GraphQLClient';
import Config from './core/Config';
import State from './core/State';
import setLocation from './reducer/setLocation';
import baseIntiialState from './initialState';
import ErrorPresenter from './service/ErrorPresenter';
import ErrorReporter from './service/ErrorReporter';
import MessageDispatcher from './service/MessageDispatcher';
import IntercomService from './service/IntercomService';
import ConfigurationStorage from './service/persistent/ConfigurationStorage';
import TranslationBackend from './service/TranslationBackend';
import AuthUserWebApi from './service/persistent/AuthUserWebApi';
import UserWebApi from './service/persistent/UserWebApi';
import WorkWebApi from './service/persistent/WorkWebApi';
import PerformanceMeasure from './service/PerformanceMeasure';
import AuthenticationUsecase from './usecase/AuthenticationUsecase';
import ListWorkUsecase from './usecase/ListWorkUsecase';
import ConfigurationChangingUsecase from './usecase/ConfigurationChangingUsecase';
import FatalErrorUsecase from './usecase/FatalErrorUsecase';
import createKukerEmitterMiddleware from './kukerEmitterMiddleware';
import Root from './view/Root';
import ShowWorkUsecase from './usecase/ShowWorkUsecase';
import execSitejs from './utility/execSitejs';
import AuthenticationWebApi from './service/persistent/AuthenticationWebApi';
import HttpClient from './adapter/HttpClient';

const main = () => {
  registerCustomElements(window.customElements);
  addLocaleData([...enLocaleData, ...jaLocaleData, { locale: 'ja-JP', parentLocale: 'ja' } as any]);
  register('ja', require('timeago.js/locales/ja'));

  const config: Config = (window as any).__kaizenconfig__;
  const appVersion: string = (window as any).__kaizenversion__;

  const initialState = {
    ...baseIntiialState,
    location: {
      pathname: window.location.pathname,
      search: window.location.search,
    },
  };

  const history = createBrowserHistory();

  const store = new Store<State>(initialState);

  const intercomService = new IntercomService(() => (window as any).Intercom, config.INTERCOM_APP_ID);

  // Adapter

  const googleAnalytics = new GoogleAnalytics((window as any).gtag, config.GA_TRACKING_ID);
  const performanceMeasure = new PerformanceMeasure({ googleAnalytics });

  new FirebaseClient({
    config: {
      apiKey: config.FIREBASE_API_KEY,
      authDomain: config.FIREBASE_AUTH_DOMAIN,
      databaseURL: config.FIREBASE_DATABASE_URL,
      projectId: config.FIREBASE_PROJECT_ID,
      storageBucket: config.FIREBASE_STORAGE_BUCKET,
      messagingSenderId: config.FIREBASE_MESSAGING_SENDER_ID,
      appId: config.FIREBASE_APP_ID,
    },
  });

  const idGraphqlClient = new GraphQLClient({
    endpointUrl: config.ID_API_GRAPHQL_ENDPOINT,
  });

  const offerGraphqlClient = new GraphQLClient({
    endpointUrl: config.HUGOT_API_OFFER_GRAPHQL_ENDPOINT,
  });

  // Service
  const errorPresenter = new ErrorPresenter({ store });

  const errorReporter = new ErrorReporter({
    store,
    dsn: config.SENTRY_API_DSN,
    environment: config.STAGE,
    release: appVersion,
  });

  const messageDispatcher = new MessageDispatcher({ store });

  const translationBackend = new TranslationBackend({ appVersion });

  const configurationStorage = new ConfigurationStorage({ storage: window.localStorage });

  const authUserWebApi = new AuthUserWebApi({ idGraphqlClient });

  const userWebApi = new UserWebApi({ offerGraphqlClient });

  const workWebApi = new WorkWebApi({ offerGraphqlClient });

  const httpClient = new HttpClient({ baseUrl: config.CORE_API_BASE_URL });

  const authenticationWebApi = new AuthenticationWebApi({ httpClient });

  // Usecase
  const authenticationUsecase = new AuthenticationUsecase({
    store,
    history,
    authenticationWebApi,
    intercomService,
    errorPresenter,
    authUserWebApi,
  });

  const configurationChangingUsecase = new ConfigurationChangingUsecase({
    store,
    configurationStorage,
    translationBackend,
  });

  const listWorkUsecase = new ListWorkUsecase({
    store,
    errorPresenter,
    userWebApi,
    workWebApi,
  });

  const showWorkUsecase = new ShowWorkUsecase({
    store,
    history,
    errorPresenter,
    workWebApi,
  });

  const fatalErrorUsecase = new FatalErrorUsecase({ intercomService });

  const dependency = {
    errorReporter,
    errorPresenter,
    messageDispatcher,
    performanceMeasure,
    authenticationUsecase,
    configurationChangingUsecase,
    listWorkUsecase,
    showWorkUsecase,
    fatalErrorUsecase,
  };

  const container = document.getElementById('app');
  let isRendered = false;

  const update = (state: State) => {
    const element = createElement(Root, { dependency, history, state, config });

    if (!isRendered) {
      isRendered = true;
      render(element, container);
      return;
    }

    hydrate(element, container);
  };

  history.listen(location => {
    // wait for document.title updates
    setTimeout(() => {
      googleAnalytics.sendPageView(document.title, window.location.href, location.pathname);
      execSitejs();
    });

    store.dispatch(setLocation(location));
  });

  store.addMiddleware(createKukerEmitterMiddleware());

  store.subscribe(() => update(store.getState()));

  update(store.getState());

  // set language
  const language = configurationStorage.loadLanguage() || window.navigator.language;
  configurationChangingUsecase.changeLanguageTemporarily(language);

  // Send initial page view
  googleAnalytics.sendPageView(document.title, window.location.href, location.pathname);

  execSitejs();
};

window.document.addEventListener('DOMContentLoaded', main);
